<script lang="ts" setup>
import ModalComponent from '@/components/ModalComponent.vue'
import formStore from '@/store/FormStore.ts'
import CheckBox from '@/components/CheckBox.vue'
import userStore from '@/store/UserStore.ts'
import { formatPrice } from '@/helper/tools.ts'
import ProductImage from '@/components/ProductImage.vue'
import { MissingAccessory } from '@/services/models/MissingAccessory.ts'
import { ref } from 'vue'

defineProps<{
  missingAccesories: MissingAccessory[]
}>()

const emit = defineEmits(['confirmClicked', 'cancelClicked', 'closeClicked'])

const selectedAccessories = ref<Record<string, number>>({})

function toggleQuantity(article: MissingAccessory): void {
  if (!selectedAccessories.value[article.id]) {
    selectedAccessories.value[article.id] = article.quantity
  } else {
    delete selectedAccessories.value[article.id]
  }
}

function onConfirmClicked() {
  Object.keys(selectedAccessories.value).forEach((key) => {
    formStore.data.selectedAccessories[key] = selectedAccessories.value[key]
  })

  formStore.data.missingAccessoriesConfirmed = true

  emit('confirmClicked')
}
</script>

<template>
  <ModalComponent
    :has-controls="true"
    :is-closable="true"
    @cancel="$emit('cancelClicked')"
    @close="$emit('closeClicked')"
    @confirm="onConfirmClicked"
  >
    <template #title>
      <div class="h2 modal__subtitle">
        {{ $t('modal.accessory_suggestion_title') }}
      </div>

      <div class="modal__update-description span">
        {{ $t('modal.accessory_suggestion_description') }}:
      </div>
    </template>

    <template #text>
      <div v-if="missingAccesories.length > 0" class="fTable is--products-table">
        <div class="fTable__tr hide-mobile">
          <div class="fTable__th column is--checkbox" />

          <div class="fTable__th column">
            <span>{{ $t('step_accessories.table_product') }}</span>
          </div>

          <div v-if="!userStore.hidePrices" class="fTable__th column is--price">
            <span>{{ $t('step_accessories.table_single_price') }}</span>
          </div>

          <div class="fTable__th column is--quantity">
            <span>{{ $t('step_accessories.table_quantity') }}</span>
          </div>
        </div>

        <div v-for="article in missingAccesories" :key="article.id" class="fTable__item">
          <div class="fTable__tr">
            <CheckBox
              :id="article.id"
              :model-value="!!selectedAccessories[article.id]"
              class="column"
              name="remoteControl"
              @update:model-value="toggleQuantity(article)"
            />

            <div class="product__info column">
              <ProductImage
                :alt-text="article.details?.name"
                :product-img="article.details?.pictureUrl"
                :rounded="false"
              />

              <div class="fTable__product-infos">
                <span>{{ article.details?.name }}</span>

                <p class="info__text">
                  {{ $t('modal.accessory_suggestion_based_on', { articles: article.origins.join(' + ') }) }}
                </p>
              </div>
            </div>

            <div class="fTable__product-infos is--price column">
              {{ formatPrice(article.details?.price) }}
            </div>

            <div class="fTable__product-infos is--quantity column">
              {{ article.quantity }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #confirm>
      {{
        missingAccesories.some(a => selectedAccessories[a.id] > 0)
          ? $t('modal.btn_continue_and_add')
          : $t('modal.btn_continue_without')
      }}
    </template>

    <template #cancel>
      {{ $t('modal.btn_cancel') }}
    </template>
  </ModalComponent>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

:deep(.modal__box) {
  width: 60rem;
}

:deep(.modal__content) {
  width: 100%;
}

:deep(.modal__btns) {
  justify-content: space-between;
}

.is--products-table {
  border: 1px solid $borderColor;

  @include themify($themes) {
    border-radius: rem-calc(themed('cardBorderRadius'));
  }

  .fTable__tr {
    background-color: unset;
    display: grid;
    grid-template-columns: 0.5fr 3fr 1fr 1fr;
    border-bottom: 1px solid $borderColor;

    &:last-child,
    &:first-child {
      border-bottom: none;
    }

    .column {
      &.is--checkbox {
        width: rem-calc(82);
      }

      &:last-child {
        padding-right: 2rem;
      }

      &.fTable__th {
        &.is--price,
        &.is--quantity {
          justify-content: end;
        }
      }

      &.fTable__product-infos {
        &.is--price,
        &.is--quantity {
          align-items: end;
        }
      }
    }
  }
}

label {
  margin-bottom: 0;
}

.product__img {
  min-width: 6rem;
  max-width: 6rem;
}

.product__info {
  display: flex;
  align-items: center;
  gap: 2rem;
  text-align: left;

  @include media('<tablet') {
    word-wrap: break-word;
  }

  span {
    font-size: 1.125rem;
    margin-bottom: 0;
  }

  .info__text {
    color: $textColorLight;
  }
}
</style>
